import { In } from 'typeorm'
import { Directive, Resolver, Query, Args, Arg, Ctx, FieldResolver, Root } from 'type-graphql'
import { Domain, getQueryBuilderFromListParams, getRepository, ListParam } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { KpiMetricValue } from './kpi-metric-value'
import { KpiMetricValueList } from './kpi-metric-value-type'
import { KpiMetric } from '../kpi-metric/kpi-metric'
import { getDomainIdsWithAncestors } from '../utils/domain-inheritance'

@Resolver(KpiMetricValue)
export class KpiMetricValueQuery {
  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiMetricValueList, { description: 'To fetch multiple KpiMetricValues' })
  async kpiMetricValues(
    @Args(type => ListParam) params: ListParam,
    @Ctx() context: ResolverContext
  ): Promise<KpiMetricValueList> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)

    const queryBuilder = getQueryBuilderFromListParams({
      domain: undefined,
      params,
      repository: getRepository(KpiMetricValue),
      searchables: ['metricId', 'group', 'valueDate']
    })

    queryBuilder.andWhere(`${queryBuilder.alias}.domain IN (:...ancestorDomainIds)`, {
      ancestorDomainIds: domainIds
    })

    const [items, total] = await queryBuilder.getManyAndCount()
    return { items, total }
  }

  @Directive('@privilege(category: "kpi", privilege: "query", domainOwnerGranted: true, superUserGranted: true)')
  @Query(returns => KpiMetricValue, {
    nullable: true,
    description: 'Fetch a single metric value by its unique identifier.'
  })
  async kpiMetricValue(
    @Arg('id', { description: 'Unique identifier of the metric value to fetch.' }) id: string,
    @Ctx() context: ResolverContext
  ): Promise<KpiMetricValue | null> {
    const { domain } = context.state
    const domainIds = await getDomainIdsWithAncestors(domain)
    return await getRepository(KpiMetricValue).findOne({
      where: { domain: { id: In(domainIds) }, id }
    })
  }

  @FieldResolver(type => Domain)
  async domain(@Root() metricValue: KpiMetricValue): Promise<Domain> {
    return metricValue.domainId && (await getRepository(Domain).findOneBy({ id: metricValue.domainId }))
  }

  @FieldResolver(type => User)
  async updater(@Root() metricValue: KpiMetricValue): Promise<User> {
    return metricValue.updaterId && (await getRepository(User).findOneBy({ id: metricValue.updaterId }))
  }

  @FieldResolver(type => User)
  async creator(@Root() metricValue: KpiMetricValue): Promise<User> {
    return metricValue.creatorId && (await getRepository(User).findOneBy({ id: metricValue.creatorId }))
  }

  @FieldResolver(type => KpiMetric)
  async metric(@Root() metricValue: KpiMetricValue): Promise<KpiMetric | null> {
    if (!metricValue.metricId) return null
    return await getRepository(KpiMetric).findOneBy({ id: metricValue.metricId })
  }
}
